import React, { useState, useEffect, useMemo, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MENU_GROUPS, MENU_ITEMS, itemAccess, roleAllows, hiddenSetFor, menuItemForPath } from '../lib/menu';
import { api } from '../lib/api';
import { LogOut, Shield, Columns3, TrendingDown, Activity, Wallet, Presentation, Users, AlertTriangle, CalendarDays, PenTool, ChevronDown, Star, Settings, ClipboardCheck, Compass, Layers, Sun, Moon, Bug, Bookmark, Sparkles, BarChart3, ListTodo, BookOpen, HeartPulse, Wrench, AtSign, QrCode, Waypoints, FlaskConical, Hand, ContactRound, Repeat, Megaphone, Inbox, PhoneCall, Mailbox, Printer, Handshake, Sprout, Target, TrendingUp, Landmark, Clock4, Heart, Coins, MonitorSmartphone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import { APP_VERSION, buildStamp } from '../version';
import { useTheme } from '../contexts/ThemeContext';
import { BugReportDialog } from './BugReportDialog';
import { InvestorModeButton } from './InvestorMode';
import { InboundCallSidebar } from './InboundCall';
import { DealSearchSidebar } from './DealSearchSidebar';
import { ClockInButton } from './ClockInButton';

const ICONS = {
  activity: Activity, funnel: TrendingDown, explorer: Compass, 'thesis-screening': Target, compare: Columns3,
  'deal-inbox': Inbox, 'broker-traffic': AtSign, 'broker-calls': Handshake, 'broker-campaigns': Megaphone, 'broker-stats': TrendingUp,
  'my-reviews': ClipboardCheck, mentions: AtSign, tasks: ListTodo, 'my-time': Clock4, favorites: Star, bookmarks: Bookmark, settings: Settings,
  meeting: CalendarDays, 'ac-meeting': Landmark, 'whos-turn': Hand, 'team-development': Sprout, reviews: ClipboardCheck,
  contacts: ContactRound, outreach: PenTool, 'outreach-sequences': Repeat, 'outreach-stats': QrCode, 'ab-live': FlaskConical, post: Mailbox, 'approval-workplace': Printer,
  health: HeartPulse, 'team-kpis': Target, calls: PhoneCall, 'infra-funds': Landmark, 'team-analytics': BarChart3,
  manual: BookOpen, 'outreach-guide': Waypoints, 'feedback-guide': Sprout,
  budget: Wallet, reporting: Presentation,
  team: Users, system: Wrench, sectors: Layers, 'scorecard-info': Shield, 'ai-costs': Coins, vestaboard: MonitorSmartphone,
};

// "last published" next to the version: build time of the production bundle (= deploy), "preview" on the dev server
const PublishedStamp = () => {
  const { short } = buildStamp();
  return short ? <span className="block text-[8px] text-muted-foreground/40" data-testid="app-published-stamp">{short}</span> : null;
};

function SidebarGroup({ label, badge = 0, badgeRed = false, openGroup, onToggle, children }) {
  // Accordion: only one group open at a time; clicking a sub-item keeps its group open.
  const open = openGroup === label;
  return (
    <div className="mb-1.5">
      <button onClick={() => onToggle(label)}
        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-sm text-xs uppercase tracking-wider font-bold border transition-colors ${
          open
            ? 'bg-secondary/70 text-foreground border-border'
            : 'bg-secondary/30 text-muted-foreground border-transparent hover:bg-secondary/60 hover:text-foreground'
        }`}>
        {label}
        <span className="flex items-center gap-1.5">
          {!open && badge > 0 && (
            <span className={`${badgeRed ? 'bg-red-500' : 'bg-amber-500'} text-white text-[9px] font-bold font-mono rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 normal-case tracking-normal`}
              data-testid={`group-badge-${label.toLowerCase().replace(/\s+/g, '-')}`}>
              {badge}
            </span>
          )}
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? '' : '-rotate-90'}`} />
        </span>
      </button>
      {open && <div className="mt-1 mb-2 ml-3 pl-2 border-l border-border/70 space-y-0.5">{children}</div>}
    </div>
  );
}

export function Sidebar({ onNavigate }) {
  const { user, logout, isAdmin, isAcMember } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [bugOpen, setBugOpen] = useState(false);
  const navigate = useNavigate();
  const [lastBackup, setLastBackup] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [recentActivity, setRecentActivity] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [actionableReviews, setActionableReviews] = useState(0);
  const [taskCounts, setTaskCounts] = useState({ open: 0, action_needed: 0, inactive_deals: 0 });
  const [mentionsCount, setMentionsCount] = useState(0);
  const [outreachStale, setOutreachStale] = useState(0);
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [dealInboxCount, setDealInboxCount] = useState(0);
  const [tdTodos, setTdTodos] = useState(0);
  const [mstodoConnected, setMstodoConnected] = useState(false);
  const [timeclock, setTimeclock] = useState(null);
  const [openGroup, setOpenGroup] = useState(() => sessionStorage.getItem('sidebar-open-group') || null);
  const toggleGroup = (label) => setOpenGroup((g) => {
    const next = g === label ? null : label;
    if (next) sessionStorage.setItem('sidebar-open-group', next);
    else sessionStorage.removeItem('sidebar-open-group');
    return next;
  });
  const handleNav = () => { if (onNavigate) onNavigate(); };
  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (isAdmin) {
      api.getBackupStatus().then(r => setLastBackup(r.data.last_backup_at)).catch(() => {});
    }
    const fetchActivity = () => {
      if (!user?.id) return;
      const isAc = user.role === 'ac_member';
      api.getMyTimeclock().then(r => setTimeclock(r.data)).catch(() => {});
      api.getGlobalActivity().then(r => {
        setUnreadCount(r.data.total_unread || 0);
        setRecentActivity((r.data.entries || []).slice(0, 8));
      }).catch(() => {});
      if (!isAc) {
        api.getPendingReviews().then(r => {
          setActionableReviews(r.data.actionable_total || 0);
        }).catch(() => {});
        api.getTaskSummary().then(r => setTaskCounts({ open: r.data.open || 0, action_needed: r.data.action_needed || 0, inactive_deals: r.data.inactive_deals || 0 })).catch(() => {});
        api.getMentionsInbox().then(r => setMentionsCount(r.data.unread_total ?? r.data.total ?? 0)).catch(() => {});
        api.mstodoStatus().then(r => setMstodoConnected(!!r.data.connected)).catch(() => {});
        api.getDealProposalCount().then(r => setDealInboxCount(r.data.count || 0)).catch(() => {});
        api.outreachSummary().then(r => {
          setOutreachStale(r.data.stale_approved || 0);
          setPendingApprovals(r.data.pending_approvals || 0);
        }).catch(() => {});
      }
      if (!isAc || user?.team_dev_access) {
        api.teamDevOverview().then(r => {
          const c = r.data.counts || {};
          setTdTodos((c.unread || 0) + (c.requests_for_me || 0) + (c.ideas_to_react || 0) + (c.checkin_done ? 0 : 1));
        }).catch(() => {});
      }
    };
    fetchActivity();
    const interval = setInterval(fetchActivity, 60000);
    return () => clearInterval(interval);
  }, [isAdmin, isAcMember, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync the PWA app-icon badge (red counter) with open items — only items the user still needs to act on.
  // My Tasks stays visible for everyone — MS To Do only mirrors explicitly pushed tasks.
  const showMyTasks = !isAcMember;
  const appBadgeTotal = (showMyTasks ? taskCounts.open || 0 : 0) + mentionsCount + actionableReviews + outreachStale + pendingApprovals;
  useEffect(() => {
    if (!('setAppBadge' in navigator)) return;
    if (appBadgeTotal > 0) navigator.setAppBadge(appBadgeTotal).catch(() => {});
    else navigator.clearAppBadge().catch(() => {});
  }, [appBadgeTotal]);

  const handleLogout = () => { logout(); navigate('/login'); };
  const reloadTimeclock = () => api.getMyTimeclock().then(r => setTimeclock(r.data)).catch(() => {});
  const handleBackup = () => {
    setDownloading(true);
    window.open(`${API_BASE}/api/admin/backup`, '_blank');
    setTimeout(() => { setDownloading(false); setLastBackup(new Date().toISOString()); }, 2000);
  };

  const daysSinceBackup = lastBackup ? Math.floor((Date.now() - new Date(lastBackup).getTime()) / (1000 * 60 * 60 * 24)) : null;
  const needsBackup = daysSinceBackup === null || daysSinceBackup >= 7;

  const linkClass = ({ isActive }) =>
    `flex items-center gap-2.5 px-3 py-2.5 rounded-sm text-sm transition-colors duration-100 ${
      isActive
        ? 'bg-blue-600/10 text-blue-400 border border-blue-600/20'
        : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
    }`;

  // Per-user menu (admin-configured in Team & Roles): role decides what CAN show, hidden_menu_items trims it. Admins see all.
  const hidden = useMemo(() => hiddenSetFor(user), [user]);
  // "My ♥ Tabs": learn which sidebar pages this user opens most (top 5, last 30 days, ≥ 3 visits)
  const location = useLocation();
  const [topPages, setTopPages] = useState([]);
  const lastVisit = useRef('');
  useEffect(() => { api.getTopPages().then(r => setTopPages(r.data.pages || [])).catch(() => {}); }, []);
  useEffect(() => {
    const it = menuItemForPath(location.pathname);
    if (!it || lastVisit.current === location.pathname) return;
    lastVisit.current = location.pathname;
    api.recordPageVisit(it.id).then(r => setTopPages(r.data.pages || [])).catch(() => {});
  }, [location.pathname]);
  const show = (item, group) => roleAllows(itemAccess(item, group), user, { timeclockEnabled: !!timeclock?.enabled || (timeclock?.visible_count || 0) > 0 }) && !hidden.has(item.id);
  const itemBadges = {
    'nav-deal-inbox': { n: dealInboxCount, cls: 'bg-sky-500', red: true, testid: 'deal-inbox-badge', title: 'AI-detected deal proposals' },
    'nav-my-reviews': { n: actionableReviews, cls: 'bg-red-500', red: true, testid: 'my-reviews-badge', title: 'Reviews waiting on you' },
    'nav-mentions': { n: mentionsCount, cls: 'bg-red-500', red: true, testid: 'mentions-badge' },
    'nav-tasks': { n: taskCounts.open, cls: taskCounts.action_needed > 0 ? 'bg-red-500' : 'bg-amber-500', red: taskCounts.action_needed > 0, testid: 'tasks-due-badge' },
    'nav-team-development': { n: tdTodos, cls: 'bg-emerald-500', testid: 'team-dev-badge', title: 'Offene Team-Development-To-dos' },
    'nav-team-development-ac': { n: tdTodos, cls: 'bg-emerald-500', testid: 'team-dev-badge-ac' },
    'nav-reviews': { n: actionableReviews, cls: 'bg-amber-500', testid: 'reviews-badge', title: 'Reviews waiting on you' },
    'nav-outreach': { n: outreachStale, cls: 'bg-red-500', red: true, testid: 'outreach-stale-badge', title: 'Approved letters not sent for 7+ days' },
  };
  const groupExtra = { outreach: pendingApprovals };
  const renderItem = (it) => {
    const Icon = ICONS[it.id];
    const b = itemBadges[it.testid];
    return (
      <NavLink key={it.testid} to={it.to} end={it.end} data-testid={it.testid} className={linkClass} onClick={handleNav}>
        <Icon className="w-4 h-4" strokeWidth={1.5} />
        {it.label}
        {b && b.n > 0 && (
          <span className={`ml-auto ${b.cls} text-white text-[9px] font-bold font-mono rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1`}
            data-testid={b.testid} title={b.title}>
            {b.n}
          </span>
        )}
      </NavLink>
    );
  };

  const roleStyle = isAdmin
    ? { bg: 'rgba(59,130,246,0.15)', color: '#93c5fd', border: 'rgba(59,130,246,0.3)', label: 'Admin' }
    : isAcMember
    ? { bg: 'rgba(168,85,247,0.15)', color: '#c4b5fd', border: 'rgba(168,85,247,0.3)', label: 'AC Member' }
    : { bg: 'rgba(16,185,129,0.15)', color: '#6ee7b7', border: 'rgba(16,185,129,0.3)', label: 'OLTA Team' };

  return (
    <TooltipProvider delayDuration={300}>
    <aside className="w-56 h-full bg-card border-r border-border flex flex-col" data-testid="sidebar">
      <div className="pt-[calc(env(safe-area-inset-top)+1.5rem)] pb-4 px-4 border-b border-border flex flex-col items-center">
        <img src="/olta-logo.png" alt="OLTA Nachfolge" className="sidebar-logo w-full max-w-[120px] h-auto mx-auto" />
        <ClockInButton status={timeclock} onChange={reloadTimeclock} />
      </div>

      <nav className="flex-1 p-3 space-y-0 overflow-y-auto custom-scrollbar">
        {/* Activity — standalone with icon + notification popover */}
        {!hidden.has('activity') && (
        <div className="mb-2 relative">
          <div className="flex items-center">
            <NavLink to="/activity" data-testid="nav-activity" className={({ isActive }) =>
              `flex-1 flex items-center gap-2.5 px-3 py-2 rounded-sm text-sm transition-colors duration-100 ${
                isActive ? 'bg-blue-600/10 text-blue-400 border border-blue-600/20' : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
              }`} onClick={handleNav}>
              <Activity className="w-4 h-4" strokeWidth={1.5} />
              Activity
            </NavLink>
            <button onClick={() => setNotifOpen(!notifOpen)} data-testid="activity-badge"
              className={`ml-1 text-[9px] font-bold font-mono rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 transition-colors ${
                unreadCount > 0 ? 'bg-blue-600 text-white hover:bg-blue-500' : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
              }`}>
              {unreadCount > 0 ? (unreadCount > 99 ? '99+' : unreadCount) : '·'}
            </button>
          </div>
          {notifOpen && (
            <div className="absolute left-0 right-0 top-10 bg-card border border-border rounded-sm shadow-xl z-50 drop-in custom-scrollbar" 
              style={{ maxHeight: '320px', overflowY: 'auto' }}
              onMouseLeave={() => setNotifOpen(false)}>
              {!isAcMember && (
                <div className="px-3 py-2 border-b border-border bg-secondary/30" data-testid="app-badge-breakdown">
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold">
                    App badge · {appBadgeTotal}
                  </span>
                  <div className="mt-1 space-y-0.5">
                    {[
                      { label: 'Open tasks', sub: taskCounts.inactive_deals > 0 ? `incl. ${taskCounts.inactive_deals} inactive deal${taskCounts.inactive_deals > 1 ? 's' : ''}` : null, count: taskCounts.open || 0, to: '/tasks' },
                      { label: 'Unread mentions', count: mentionsCount, to: '/mentions' },
                      { label: 'Reviews awaiting you', count: actionableReviews, to: '/reviews' },
                      { label: 'Letters approved, not sent (7d+)', count: outreachStale, to: '/outreach' },
                      { label: 'Letters waiting for your approval', count: pendingApprovals, to: '/outreach' },
                    ].map(r => (
                      <NavLink key={r.label} to={r.to} onClick={() => { setNotifOpen(false); handleNav(); }}
                        className="flex items-center justify-between gap-2 text-[11px] text-foreground/80 hover:text-blue-400 transition-colors py-0.5">
                        <span className="truncate">{r.label}{r.sub && <span className="text-muted-foreground/60"> · {r.sub}</span>}</span>
                        <span className={`font-mono font-bold shrink-0 ${r.count > 0 ? 'text-red-400' : 'text-muted-foreground/40'}`}>{r.count}</span>
                      </NavLink>
                    ))}
                  </div>
                </div>
              )}
              <div className="px-3 py-2 border-b border-border flex items-center justify-between">
                <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold">Recent Activity</span>
                <NavLink to="/activity" onClick={() => { setNotifOpen(false); handleNav(); }}
                  className="text-[10px] text-blue-400 hover:text-blue-300">View all</NavLink>
              </div>
              {recentActivity.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">No recent activity</p>
              ) : recentActivity.map((e, i) => (
                <div key={i} className="px-3 py-2 border-b border-border/50 hover:bg-secondary/20 transition-colors">
                  <p className="text-[11px] text-foreground/80 leading-snug">{e.detail?.slice(0, 80)}{e.detail?.length > 80 ? '...' : ''}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[9px] text-muted-foreground/50">{e.user_name}</span>
                    <span className="text-[9px] text-muted-foreground/30">{new Date(e.at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</span>
                    {e.project_name && <span className="text-[9px] text-blue-400/40">{e.project_name}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Deal search — type-ahead, click to open the deal */}
        <DealSearchSidebar onNavigate={handleNav} />

        {/* Inbound Call — instant caller briefing (non-AC) */}
        {!isAcMember && <InboundCallSidebar />}

        {!hidden.has('my-tabs') && (() => {
          const tabs = topPages.map(tp => {
            const it = MENU_ITEMS.find(x => x.id === tp.id);
            const group = it && MENU_GROUPS.find(g => g.id === it.groupId);
            return it && group && show(it, group) ? { ...it, n: tp.n } : null;
          }).filter(Boolean).slice(0, 5);
          if (tabs.length === 0) return null;
          return (
            <div className="mb-2 pb-2 border-b border-border/60" data-testid="my-tabs">
              <div className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold text-muted-foreground" title="Deine meistgenutzten Seiten der letzten 30 Tage — lernt automatisch">
                <Heart className="w-3 h-3 text-rose-400 fill-rose-400/60" /> My Tabs
              </div>
              {tabs.map(it => {
                const Icon = ICONS[it.id];
                return (
                  <NavLink key={it.id} to={it.to} end={it.end} data-testid={`mytab-${it.id}`} className={linkClass} onClick={handleNav}
                    title={`${it.n} Aufrufe in den letzten 30 Tagen`}>
                    <Icon className="w-4 h-4" strokeWidth={1.5} />
                    {it.label}
                    <span className="ml-auto text-[9px] font-mono text-muted-foreground/70" data-testid={`mytab-count-${it.id}`}>{it.n}</span>
                  </NavLink>
                );
              })}
            </div>
          );
        })()}
        {MENU_GROUPS.map(group => {
          if (group.id === 'activity' || group.id === 'my-tabs') return null;
          const items = group.items.filter(it => show(it, group));
          if (items.length === 0) return null;
          if (group.standalone) {
            return items.map(it => renderItem(it));
          }
          const counts = items.map(it => itemBadges[it.testid]).filter(b => b && b.n > 0);
          const badge = counts.reduce((s, b) => s + b.n, 0) + (groupExtra[group.id] || 0);
          return (
            <SidebarGroup key={group.id} label={group.label} openGroup={openGroup} onToggle={toggleGroup}
              badge={badge} badgeRed={counts.some(b => b.red)}>
              {items.map(it => renderItem(it))}
            </SidebarGroup>
          );
        })}
      </nav>

      <div className="px-3 pt-2 pb-2 border-t border-border">
        <div className="flex items-center gap-2.5 py-1.5">
          <div className="w-8 h-8 rounded-full bg-blue-600/20 flex items-center justify-center text-blue-400 text-xs font-medium font-mono shrink-0">
            {user?.name?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="flex-1 min-w-0 text-xs text-foreground truncate" title={user?.name}>{user?.name}</p>
              <span className="shrink-0 text-[9px] px-1 py-0 rounded-sm font-mono uppercase"
                style={{ background: roleStyle.bg, color: roleStyle.color, border: `1px solid ${roleStyle.border}` }}
                data-testid="user-role-badge"
              >
                {roleStyle.label}
              </span>
            </div>
            <p className="text-[10px] text-muted-foreground truncate" title={user?.email}>{user?.email}</p>
          </div>
        </div>
        <div className="flex items-center justify-between gap-1 mt-1.5 pt-1.5 border-t border-border/40">
          <div className="flex items-center gap-0.5">
            <Button variant="ghost" size="sm" data-testid="theme-toggle-button" onClick={toggleTheme}
              className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground" title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}>
              {theme === 'light' ? <Moon className="w-3.5 h-3.5" /> : <Sun className="w-3.5 h-3.5" />}
            </Button>
            <Button variant="ghost" size="sm" data-testid="report-bug-btn" onClick={() => setBugOpen(true)}
              className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground" title="Report a bug">
              <Bug className="w-3.5 h-3.5" />
            </Button>
            <Button variant="ghost" size="sm" data-testid="whats-new-btn" onClick={() => window.dispatchEvent(new Event('open-whats-new'))}
              className="h-7 w-7 p-0 text-muted-foreground hover:text-blue-400" title="What's new">
              <Sparkles className="w-3.5 h-3.5" />
            </Button>
            <InvestorModeButton />
            <Button variant="ghost" size="sm" data-testid="logout-button" onClick={handleLogout}
              className="h-7 w-7 p-0 text-muted-foreground hover:text-red-400" title="Log out">
              <LogOut className="w-3.5 h-3.5" />
            </Button>
          </div>
          {isAdmin ? (
          <button onClick={() => window.dispatchEvent(new Event('open-whats-new'))}
            className="text-[9px] text-muted-foreground/40 hover:text-blue-400 font-mono tracking-wider transition-colors text-right" data-testid="app-version" title={`What's new · ${buildStamp().long}`}>
            v{APP_VERSION}<PublishedStamp />
          </button>
          ) : (
            <span className="text-[9px] text-muted-foreground/40 font-mono tracking-wider text-right" data-testid="app-version" title={buildStamp().long}>v{APP_VERSION}<PublishedStamp /></span>
          )}
        </div>
      </div>
      <BugReportDialog open={bugOpen} onOpenChange={setBugOpen} />
    </aside>
    </TooltipProvider>
  );
}
